const GENERIC_STATUS_MESSAGE_REGEX = /^Request failed with status code \d+$/i;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const collectValidationMessages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectValidationMessages(entry));
  }

  if (isNonEmptyString(value)) {
    return [value.trim()];
  }

  if (value && typeof value === 'object') {
    return Object.values(value).flatMap((entry) => collectValidationMessages(entry));
  }

  return [];
};

export const getApiErrorMessage = (
  error: unknown,
  fallback = 'Unable to complete the request. Please try again.'
): string => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const maybeError = error as {
    data?: any;
    response?: { data?: any };
    message?: unknown;
    error?: unknown;
  };

  const apiData = maybeError.data ?? maybeError.response?.data;

  if (typeof apiData === 'string' && apiData.trim()) {
    return apiData.trim();
  }

  const validationMessages = collectValidationMessages(apiData?.errors);
  if (validationMessages.length > 0) {
    return Array.from(new Set(validationMessages)).join(' ');
  }

  if (isNonEmptyString(apiData?.message)) {
    return apiData.message.trim();
  }

  if (isNonEmptyString(apiData?.detail)) {
    return apiData.detail.trim();
  }

  if (isNonEmptyString(apiData?.title)) {
    return apiData.title.trim();
  }

  if (isNonEmptyString(maybeError.error)) {
    return maybeError.error.trim();
  }

  if (isNonEmptyString(maybeError.message) && !GENERIC_STATUS_MESSAGE_REGEX.test(maybeError.message.trim())) {
    return maybeError.message.trim();
  }

  return fallback;
};
